import { inject, Injectable } from "@angular/core";
import { HttpContext } from "@angular/common/http";
import { catchError, map, Observable, of, retry, shareReplay } from "rxjs";
import { UsersTenantContractResponseDto, UsersTenantsService } from "@o2-fe/api";
import { SUPPRESS_ERROR_TOAST, SUPPRESS_SUCCESS_TOAST } from "@o2-fe/toast-message";
import { User } from "../interfaces/user.interface";

/** Delay between the retries of the read, so a network blip does not let the user in for the session. */
const READ_RETRY_DELAY_MS = 2000;

/** Retries of the read. */
const READ_RETRIES = 3;

/**
 * Knows whether the tenant of the logged user has a contract sold by Rocket still waiting for acceptance.
 * The contract is read once per session and shared by the guards: while it is pending the app routes send
 * every user to /contract. A failed read lets the user in, as nothing can be accepted without the contract, but it
 * is not kept: the next navigation reads again.
 */
@Injectable({ providedIn: "root" })
export class ContractGateService {
  private readonly _tenantsService = inject(UsersTenantsService);

  /** The read of the session, shared by every guard until the contract is accepted. */
  private _pending$?: Observable<UsersTenantContractResponseDto | null>;

  /**
   * Returns the contract waiting for acceptance, or null when there is none.
   * @param user the logged user.
   * @returns the pending contract, or null.
   */
  pending$(user: User): Observable<UsersTenantContractResponseDto | null> {
    if (!user.tenant) return of(null);
    this._pending$ ??= this._tenantsService
      .tenantContractControllerFindOne(
        { tenant: user.tenant },
        new HttpContext().set(SUPPRESS_ERROR_TOAST, true).set(SUPPRESS_SUCCESS_TOAST, true),
      )
      .pipe(
        retry({ count: READ_RETRIES, delay: READ_RETRY_DELAY_MS }),
        map((contract) => (contract?.id ? contract : null)),
        // A failed read lets the user in this time only: the next navigation reads again.
        catchError(() => {
          this._pending$ = undefined;
          return of(null);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this._pending$;
  }

  /** Records that the contract has been accepted: the app routes open again. */
  markAccepted(): void {
    this._pending$ = of(null);
  }
}
